import { AxiosError } from 'axios';
import { Navigate, getRouteApi } from '@tanstack/react-router';
import GoBackHeader from '@emfood/shared/src/components/go-back-header/go-back-header';
import {
  createSortSchema,
  ACHelmetContainer,
  createPaginationSchema,
  useSyncTableSearchParams,
  getTableModelsFromSearchParams,
} from '@emfood/shared';

import { Card, Button, CardContent } from '@mui/material';

import { paths } from 'src/routes/paths';

import { useOrders } from 'src/hooks/use-orders';

import { useSettingsContext } from 'src/components/settings';

import OrdersTable from './orders-table';

export const OrdersListSearchParamsSchema = createPaginationSchema().concat(
  createSortSchema(
    ['orderReferenceNumber', 'createdOn', 'deliveryDate', 'distributorName'] as const,
    { defaultSortBy: 'createdOn', defaultSortOrder: 'desc' }
  )
);

const routeApi = getRouteApi('/orders/');

export default function PreviousOrdersList() {
  const settings = useSettingsContext();
  const navigate = routeApi.useNavigate();
  const routeSearch = routeApi.useSearch();

  const { paginationModel: searchPaginationModel, sortModel: searchSortModel } =
    getTableModelsFromSearchParams(routeSearch);

  const {
    orders,
    ordersTotalCount,
    ordersTotalPages,
    fetchOrdersError,
    fetchOrdersLoading,
    paginationModel,
    sortModel,
    setPaginationModel,
    setSortModel,
  } = useOrders({
    initialPaginationModel: searchPaginationModel,
    initialSortModel: searchSortModel,
  });

  useSyncTableSearchParams({ navigate, paginationModel, sortModel });

  if (
    fetchOrdersError &&
    fetchOrdersError instanceof AxiosError &&
    (fetchOrdersError.status === 404 || fetchOrdersError.status === 403)
  ) {
    return <Navigate to={`/${fetchOrdersError.status}`} />;
  }

  return (
    <ACHelmetContainer title="Orders" maxWidth={settings.themeStretch ? false : 'xl'}>
      <GoBackHeader
        heading="Orders"
        disableGoBack
        action={
          <Button
            variant="contained"
            disabled={!orders}
            onClick={() => navigate({ to: paths.dashboard.orders.export })}
          >
            Export Orders
          </Button>
        }
      />
      <Card>
        <CardContent>
          <OrdersTable
            orders={orders}
            ordersTotalPages={ordersTotalPages}
            isLoading={fetchOrdersLoading}
            ordersTotalCount={ordersTotalCount}
            paginationModel={paginationModel}
            sortModel={sortModel}
            onSortChanged={setSortModel}
            onPaginationModelChanged={setPaginationModel}
          />
        </CardContent>
      </Card>
    </ACHelmetContainer>
  );
}
